/**
 * Allowlist of AG Grid ColDef and ColGroupDef properties for ag-grid-community@35.2.1.
 *
 * Source: `colDefPropertyMap` in `node_modules/ag-grid-community/dist/package/main.esm.mjs`.
 * That map is what the grid checks when it logs "invalid colDef property". The same
 * keys are declared on AbstractColDef, ColDef, and ColGroupDef in
 * `dist/types/src/entities/colDef.d.ts`, plus IFilterDef in
 * `dist/types/src/interfaces/iFilter.d.ts` (filter, filterParams,
 * floatingFilterComponent, floatingFilterComponentParams).
 *
 * Unknown keys are dropped. They are not copied onto `context`. Callbacks in this
 * repo only read valid ColDef fields from the grid (`field`, `colId`, `headerName`,
 * `valueFormatter`). Extra app metadata (`sortType`, `materialId`, `metadata`, and
 * grid options that were placed on a column) stays on the original config, which
 * this helper does not mutate. An existing `context` value is copied through.
 */

export const COL_DEF_KEYS = new Set([
  "aggFunc",
  "allowFormula",
  "allowedAggFuncs",
  "autoHeaderHeight",
  "autoHeight",
  "cellAriaRole",
  "cellClass",
  "cellClassRules",
  "cellDataType",
  "cellEditor",
  "cellEditorParams",
  "cellEditorPopup",
  "cellEditorPopupPosition",
  "cellEditorSelector",
  "cellRenderer",
  "cellRendererParams",
  "cellRendererSelector",
  "cellStyle",
  "chartDataType",
  "checkboxSelection",
  "children",
  "colId",
  "colSpan",
  "columnChooserParams",
  "columnGroupShow",
  "comparator",
  "context",
  "contextMenuItems",
  "dateComponent",
  "dateComponentParams",
  "defaultAggFunc",
  "dndSource",
  "dndSourceOnRowDrag",
  "editable",
  "enableCellChangeFlash",
  "enablePivot",
  "enableRowGroup",
  "enableValue",
  "equals",
  "field",
  "filter",
  "filterParams",
  "filterValueGetter",
  "flex",
  "floatingFilter",
  "floatingFilterComponent",
  "floatingFilterComponentParams",
  "getFindText",
  "getQuickFilterText",
  "groupHierarchy",
  "groupId",
  "groupRowEditable",
  "groupRowValueSetter",
  "headerCheckboxSelection",
  "headerCheckboxSelectionCurrentPageOnly",
  "headerCheckboxSelectionFilteredOnly",
  "headerClass",
  "headerComponent",
  "headerComponentParams",
  "headerGroupComponent",
  "headerGroupComponentParams",
  "headerName",
  "headerStyle",
  "headerTooltip",
  "headerTooltipValueGetter",
  "headerValueGetter",
  "hide",
  "icons",
  "initialAggFunc",
  "initialFlex",
  "initialHide",
  "initialPinned",
  "initialPivot",
  "initialPivotIndex",
  "initialRowGroup",
  "initialRowGroupIndex",
  "initialSort",
  "initialSortIndex",
  "initialWidth",
  "keyCreator",
  "loadingCellRenderer",
  "loadingCellRendererParams",
  "loadingCellRendererSelector",
  "lockPinned",
  "lockPosition",
  "lockVisible",
  "mainMenuItems",
  "marryChildren",
  "maxWidth",
  "menuTabs",
  "minWidth",
  "onCellClicked",
  "onCellContextMenu",
  "onCellDoubleClicked",
  "onCellValueChanged",
  "openByDefault",
  "pinned",
  "pivot",
  "pivotComparator",
  "pivotIndex",
  "pivotKeys",
  "pivotTotalColumnIds",
  "pivotValueColumn",
  "refData",
  "resizable",
  "rowDrag",
  "rowDragText",
  "rowGroup",
  "rowGroupIndex",
  "rowGroupingHierarchy",
  "rowSpan",
  "showDisabledCheckboxes",
  "showRowGroup",
  "singleClickEdit",
  "sort",
  "sortIndex",
  "sortable",
  "sortingOrder",
  "spanRows",
  "suppressAutoSize",
  "suppressColumnsToolPanel",
  "suppressFillHandle",
  "suppressFiltersToolPanel",
  "suppressFloatingFilterButton",
  "suppressHeaderContextMenu",
  "suppressHeaderFilterButton",
  "suppressHeaderKeyboardEvent",
  "suppressHeaderMenuButton",
  "suppressKeyboardEvent",
  "suppressMovable",
  "suppressNavigable",
  "suppressPaste",
  "suppressSizeToFit",
  "suppressSpanHeaderHeight",
  "suppressStickyLabel",
  "toolPanelClass",
  "tooltipComponent",
  "tooltipComponentParams",
  "tooltipComponentSelector",
  "tooltipField",
  "tooltipValueGetter",
  "type",
  "unSortIcon",
  "useValueFormatterForExport",
  "useValueParserForImport",
  "valueFormatter",
  "valueGetter",
  "valueParser",
  "valueSetter",
  "width",
  "wrapHeaderText",
  "wrapText",
]);

export function sanitizeColDef(colDef) {
  if (colDef == null || typeof colDef !== "object" || Array.isArray(colDef)) return colDef;

  const sanitized = {};
  for (const key of Object.keys(colDef)) {
    if (!COL_DEF_KEYS.has(key)) continue;
    if (key === "children" && Array.isArray(colDef.children)) {
      sanitized.children = sanitizeColDefs(colDef.children);
    } else {
      sanitized[key] = colDef[key];
    }
  }
  return sanitized;
}

export function sanitizeColDefs(colDefs) {
  if (!Array.isArray(colDefs)) return colDefs;
  return colDefs.map((colDef) => sanitizeColDef(colDef));
}
